// ═══════════════════════════════════════════════════════════════
// Rule Attachment — form-config versioned rule bindings
// Attaches a business rule to a stage + scope with auto-derived tier.
// ═══════════════════════════════════════════════════════════════
import { EDIT_CAPABLE_STAGES } from './behaviors.js';
/** Rule types that run in the field-level engine (web worker / client). */
export const FIELD_ENGINE_TYPES = [
    'validate',
    'substitute',
    'format',
    'calculate',
    'copy',
    'crossField',
];
/** Rule types that run in the workflow engine (server-side). */
export const WORKFLOW_ENGINE_TYPES = [
    'hold',
    'escalate',
    'reject',
    'route',
    'skip_step',
    'reclassify',
    'notify',
    'set_field',
    'reprioritize',
];
// --- Action types ---
//
// P6 T1 — ONE canonical action-type vocabulary. This runtime tuple is the single
// source of truth for the `RuleActionType` union (derived below via `typeof … [number]`)
// AND for the LIVE/DEAD classification the tripwire enforces
// (automation/__tests__/action-vocabulary.test.ts).
//
// Governing principle: stored content outlives types. Every string a persisted
// `ActionConfig.type` might carry lives here — the live ones AND the dead ones.
// The DEAD members have NO runtime executor; they are kept (never deleted),
// annotated below, ledgered in effect-registry `RETIRED` (origin 'action_type'),
// and tripwired, so the live/dead boundary can't silently blur. This union is the
// SUPERSET of the two historical unions (this file's + types/rules.ts's), which
// re-exports it — so nothing either union could represent is dropped.
/** The one canonical action-type vocabulary (LIVE ∪ DEAD). */
export const CANONICAL_ACTION_TYPES = [
    // ── LIVE: has a runtime executor (validate/check + workflow actions) ──
    'validate',
    'uniqueness',
    'historicalRange',
    'webhook_validate',
    'hold', 'escalate', 'reject', 'route',
    'skip_step', 'reclassify', 'notify', 'set_field', 'reprioritize',
    // ── DEAD: persists in stored content, NO executor. Kept, never deleted.
    //    See effect-registry RETIRED ledger (origin 'action_type'). ──
    /** DEAD — no executor (closest live: populate literal, different moment). */
    'default',
    /** DEAD — no executor; replaced by the `substitute` behavior. */
    'substitute',
    /** DEAD — no executor; replaced by the `transform` (reformat) behavior. */
    'format',
    /** DEAD — no executor; replaced by the `compute` behavior. */
    'calculate',
    /** DEAD — no live equivalent. */
    'copy',
    /** DEAD — not authorable as an action; live only as a rule-type condition mechanism. */
    'crossField',
    /** DEAD — no live equivalent (LookupAutoFillConfig had zero consumers, deleted P6 T2). */
    'lookup',
    /** DEAD — would fail-closed if wired. */
    'dateBoundary',
    /** DEAD — dead stub, no runtime handler; replaced by the `webhook_lookup` behavior. */
    'webhook_transform',
    /** DEAD — no standalone executor; expression lives as a condition/value source
     *  and as a `set_field` valueKind, not as an action in its own right. */
    'expression',
];
/**
 * Action-type strings that persist in stored content but have NO live executor.
 * Kept, NEVER deleted (a stored `ActionConfig.type` may still carry any of these);
 * single-sourced here, ledgered in effect-registry `RETIRED` (origin 'action_type'),
 * and tripwired in `automation/__tests__/action-vocabulary.test.ts`.
 */
export const DEAD_ACTION_TYPES = new Set([
    'default', 'substitute', 'format', 'calculate', 'copy',
    'crossField', 'lookup', 'dateBoundary', 'webhook_transform', 'expression',
]);
/** Canonical action types WITH a live runtime path (validate/check + workflow actions). */
export const LIVE_ACTION_TYPES = new Set(CANONICAL_ACTION_TYPES.filter((t) => !DEAD_ACTION_TYPES.has(t)));
const FIELD_ENGINE_ACTION_TYPES = new Set([
    'validate', 'default', 'substitute', 'format', 'calculate', 'copy',
    'crossField', 'lookup', 'dateBoundary',
]);
/**
 * Returns true if the given type string is a field-engine action type
 * (runs client-side in the field-level engine).
 */
export function isFieldEngineType(type) {
    return FIELD_ENGINE_ACTION_TYPES.has(type);
}
/** True for server-data validation rule types (run server-side at the batch gate).
 *  WU-3.2 T4 — `webhook` joined this set so its authoring UI reuses the same
 *  field-scope lock / condition-hiding / validationType-seeding machinery as
 *  uniqueness/historicalRange (see `checkRuleCompleteness` in
 *  rule-condition-validators.ts, which now also requires a fieldRef for
 *  webhook rules — desired, since a webhook validation rule is field-scoped
 *  just like the other two). */
export function isServerDataType(type) {
    return type === 'uniqueness' || type === 'historicalRange' || type === 'webhook';
}
/** Workflow-action rule types — dispatched by the workflow engine (Slice 4c).
 *  Single source: alias of WORKFLOW_ENGINE_TYPES (same 7 strings). */
export const ACTION_TYPES = WORKFLOW_ENGINE_TYPES;
const ACTION_TYPE_SET = new Set(ACTION_TYPES);
/** True for workflow-action rule types (route/hold/escalate/reclassify/skip_step/notify/reject). */
export function isActionType(type) {
    return ACTION_TYPE_SET.has(type);
}
/** R-263 — batch balance checkpoint triggers. Batch scope only (editor-gated);
 *  evaluated by runBalanceRules, EXCLUDED from the generic export batch gate. */
export const BALANCE_TRIGGERS = ['on-batch-classified', 'on-batch-complete'];
const BALANCE_TRIGGER_SET = new Set(BALANCE_TRIGGERS);
/** True for batch balance checkpoint triggers (on-batch-classified / on-batch-complete). */
export function isBalanceTrigger(trigger) {
    return BALANCE_TRIGGER_SET.has(trigger);
}
// --- Backend-requiring types (for tier derivation) ---
const BACKEND_TYPES = new Set([
    'webhook',
    'custom-script',
    'uniqueness',
    'historicalRange',
    'hold',
    'escalate',
    'reject',
    'route',
    'skip_step',
    'reclassify',
    'notify',
]);
// --- deriveTier ---
/**
 * Derives the execution tier (1 = client, 2 = server, 3 = batch) from the
 * rule's scope, trigger, and type.
 *
 * See the tier derivation table in the design doc for the full matrix.
 */
export function deriveTier(scope, trigger, type) {
    // batch scope → always tier 3
    if (scope === 'batch')
        return 3;
    // workflow triggers → always tier 2 (server-side)
    if (trigger === 'on-step-enter' || trigger === 'on-export' || trigger === 'on-schedule' ||
        trigger === 'on-batch-classified' || trigger === 'on-batch-complete') {
        return 2;
    }
    // form scope → tier 2 (cross-field = server)
    if (scope === 'form')
        return 2;
    // field scope + field trigger: depends on whether type needs backend
    if (BACKEND_TYPES.has(type))
        return 2;
    return 1;
}
/**
 * Creates a `RuleAttachment` with sensible defaults. Tier is auto-derived
 * unless explicitly provided.
 */
export function createRuleAttachment(input) {
    const tier = input.tier ?? deriveTier(input.scope, input.trigger, input.type);
    return {
        ...input,
        id: input.id ?? globalThis.crypto.randomUUID(),
        tier,
        isActive: input.isActive ?? true,
        displayOrder: input.displayOrder ?? 0,
    };
}
// --- Summary generator ---
/**
 * Generates a concise human-readable summary of a rule attachment.
 * Useful for admin UI tooltips and audit logs.
 */
export function generateRuleSummary(rule) {
    const parts = [];
    if (!rule.isActive) {
        parts.push('[DISABLED]');
    }
    parts.push(`"${rule.name}"`);
    parts.push(`\u2014 ${rule.scope} scope,`);
    parts.push(`${rule.trigger} at ${rule.stage},`);
    parts.push(`type: ${rule.type},`);
    parts.push(`tier ${rule.tier}`);
    if (rule.fieldRef) {
        parts.push(`(field: ${rule.fieldRef})`);
    }
    if (rule.overrideBehavior) {
        parts.push(`[override: ${rule.overrideBehavior}]`);
    }
    return parts.join(' ');
}
// --- Migration helper ---
/**
 * Converts a legacy `RuleAttachment` (with only `config`) into one that
 * always has an `actions` array.
 *
 * - If the rule already has an `actions` array (including an explicit `[]`),
 *   that array is preserved as-is. An empty array is a meaningful state — the
 *   operator has deliberately removed all actions, and the publish gate
 *   (`rule.actions.empty`) will surface the issue at publish time.
 * - Only legacy rules where `actions` is **absent** (`undefined`) get a
 *   single-element array synthesised from `rule.type` + `rule.config`. This
 *   keeps backwards compatibility with rules persisted before the actions
 *   array existed without re-injecting a default on every save round-trip.
 *
 * R-44 (2026-05-14) — previously, the check was `rule.actions && rule.actions.length > 0`,
 * which meant an explicit empty array was treated the same as a missing one
 * and triggered the synthesise branch. That defeated the Bundle 3
 * `rule.actions.empty` publish-gate finding because saving a rule with no
 * actions silently re-introduced the default `validate` action. The new
 * check distinguishes "actions was never set" (legacy) from "actions is
 * empty" (deliberate).
 */
export function migrateRuleConfig(rule) {
    if (rule.actions !== undefined) {
        return rule;
    }
    const action = {
        id: `${rule.id}-a0`,
        type: rule.type,
        config: rule.config,
        severity: rule.severity,
        displayOrder: 0,
    };
    if (rule.fieldRef) {
        action.targetField = rule.fieldRef;
    }
    return { ...rule, actions: [action] };
}
/**
 * F-036/E-032 — the stages a validate rule actually fires at.
 *
 * - `stages` present → authored multi-stage selection, honored verbatim.
 * - legacy `stage: 'verification'` → all edit-capable stages. This preserves
 *   the observable pre-fix behavior: the station always validated as
 *   'verification' regardless of the doc's step, so every 'verification' rule
 *   fired at every station visit. Expanding keeps those rules working when
 *   the station threads real stages.
 * - any other single stage → honored as authored (these rules were silently
 *   dead before the station fix — F-036).
 */
export function effectiveValidateStages(rule) {
    if (rule.stages && rule.stages.length > 0)
        return rule.stages;
    if (rule.stage === 'verification')
        return EDIT_CAPABLE_STAGES;
    return [rule.stage];
}
//# sourceMappingURL=rule-attachment.js.map